/**
 * 企业微信 JSAPI 初始化工具类
 * 用于统一管理企业微信 JSAPI 的初始化，避免重复调用
 */

import * as ww from '@wecom/jssdk'
import {
  getWecomConfigSignatureApi,
  getWecomAgentConfigSignatureApi
} from '~/modules/talents-module/apis/talents.js'
import { getWecomConfigApi } from '~/modules/management-backend/api/management.js'

class WeComJSAPI {
  constructor() {
    // 单例模式
    if (WeComJSAPI.instance) {
      return WeComJSAPI.instance
    }
    WeComJSAPI.instance = this
    
    // 是否已经初始化
    this.isInitialized = false
    // 初始化 Promise，用于避免重复初始化
    this.initPromise = null
    // 缓存配置（corpId 和 agentId）
    this.configCache = null
    // 签名缓存（key: url, value: {timestamp, nonceStr, signature, expireTime}）
    this.signatureCache = new Map()
    // 签名缓存有效期（毫秒），默认5分钟
    this.cacheExpireTime = 5 * 60 * 1000
  }

  /**
   * 获取企业微信配置（带缓存）
   * 从返回的 data 数组中取第一个元素
   * @returns {Promise<{corpId: string, agentId: string}>}
   */
  async getConfigWithCache() {
    // 如果缓存存在，直接返回
    if (this.configCache) {
      return this.configCache
    }
    
    try {
      // 调用 API 获取企业微信配置，返回 data 数组，取第一个元素
      const res = await getWecomConfigApi()
      if (res.code === 200 && res.data && Array.isArray(res.data) && res.data.length > 0) {
        // 取第一个元素
        const firstItem = res.data[0]
        this.configCache = {
          corpId: firstItem.corpId,
          agentId: firstItem.agentId
        }
        return this.configCache
      }
      throw new Error('获取企业微信配置失败：返回数据为空或格式不正确')
    } catch (error) {
      console.error('获取企业微信配置失败:', error)
      throw error
    }
  }

  /**
   * 获取配置签名（带缓存）
   * 后端会根据 token 自动获取 corpId，只需要传入 url
   * @param {string} url - 需要签名的 URL
   * @returns {Promise<{timestamp, nonceStr, signature}>}
   */
  async getConfigSignature(url) {
    // 检查缓存
    const cacheKey = `config_${url}`
    const cached = this.signatureCache.get(cacheKey)
    if (cached && Date.now() < cached.expireTime) {
      return {
        timestamp: cached.timestamp,
        nonceStr: cached.nonceStr,
        signature: cached.signature
      }
    }

    try {
      // 调用 API 获取签名，只需要传入 url，后端会处理 corpId
      const res = await getWecomConfigSignatureApi(url)
      console.log('[WeCom] getConfigSignature API 响应:', res)
      if (res.code === 200 && res.data) {
        const signatureData = {
          timestamp: res.data.timestamp,
          nonceStr: res.data.nonceStr,
          signature: res.data.signature,
          expireTime: Date.now() + this.cacheExpireTime
        }
        // 缓存签名
        this.signatureCache.set(cacheKey, signatureData)
        
        // 返回签名数据（格式：{timestamp, nonceStr, signature}）
        const result = {
          timestamp: signatureData.timestamp,
          nonceStr: signatureData.nonceStr,
          signature: signatureData.signature
        }
        console.log('[WeCom] getConfigSignature 返回结果:', result)
        return result
      }
      throw new Error('获取配置签名失败')
    } catch (error) {
      console.error('[WeCom] 获取配置签名失败:', error)
      throw error
    }
  }

  /**
   * 获取应用签名（带缓存）
   * 后端会根据 token 自动获取 corpId 和 agentId，只需要传入 url
   * @param {string} url - 需要签名的 URL
   * @returns {Promise<{timestamp, nonceStr, signature}>}
   */
  async getAgentConfigSignature(url) {
    // 检查缓存
    const cacheKey = `agent_${url}`
    const cached = this.signatureCache.get(cacheKey)
    if (cached && Date.now() < cached.expireTime) {
      return {
        timestamp: cached.timestamp,
        nonceStr: cached.nonceStr,
        signature: cached.signature
      }
    }

    try {
      // 调用 API 获取签名，只需要传入 url，后端会处理 corpId 和 agentId
      const res = await getWecomAgentConfigSignatureApi(url)
      console.log('[WeCom] getAgentConfigSignature API 响应:', res)
      if (res.code === 200 && res.data) {
        const signatureData = {
          timestamp: res.data.timestamp,
          nonceStr: res.data.nonceStr,
          signature: res.data.signature,
          expireTime: Date.now() + this.cacheExpireTime
        }
        // 缓存签名
        this.signatureCache.set(cacheKey, signatureData)
        
        // 返回签名数据（格式：{timestamp, nonceStr, signature}）
        const result = {
          timestamp: signatureData.timestamp,
          nonceStr: signatureData.nonceStr,
          signature: signatureData.signature
        }
        console.log('[WeCom] getAgentConfigSignature 返回结果:', result)
        return result
      }
      throw new Error('获取应用签名失败')
    } catch (error) {
      console.error('[WeCom] 获取应用签名失败:', error)
      throw error
    }
  }

  /**
   * 初始化企业微信 JSAPI
   * @param {Object} options - 配置选项
   * @param {Array<string>} options.jsApiList - 需要使用的 JSAPI 列表（默认：['getExternalContact', 'selectEnterpriseContact']）
   * @param {Function} options.onConfigSuccess - 配置成功回调
   * @param {Function} options.onConfigFail - 配置失败回调
   * @returns {Promise<void>}
   */
  async init(options = {}) {
    console.log('init: ', options);
    
    // 如果正在初始化，等待之前的初始化完成
    if (this.initPromise) {
      return this.initPromise
    }

    // 如果已经初始化，直接返回
    if (this.isInitialized) {
      return Promise.resolve()
    }

    // 检查是否在企业微信环境中
    if (!/MicroMessenger/i.test(navigator.userAgent)) {
      console.warn('不在企业微信环境中，跳过 JSAPI 初始化')
      return Promise.resolve()
    }

    // 创建初始化 Promise
    this.initPromise = this._doInit(options)
      .then(() => {
        this.isInitialized = true
        this.initPromise = null
      })
      .catch((error) => {
        this.initPromise = null
        throw error
      })

    return this.initPromise
  }

  /**
   * 执行初始化
   * @private
   */
  async _doInit(options) {
    const {
      jsApiList = ['getExternalContact', 'selectEnterpriseContact'],
      onConfigSuccess,
      onConfigFail
    } = options

    // 从后端获取 corpId 和 agentId
    const config = await this.getConfigWithCache()
    const corpId = config.corpId
    // 确保 agentId 是数字类型
    const agentId = parseInt(config.agentId)

    // 创建签名函数，只需要传入 url，后端会处理
    // 使用箭头函数绑定 this，避免方法名冲突
    const getConfigSignatureFn = async (url) => {
      console.log('[WeCom] getConfigSignature 被调用, url:', url)
      try {
        const result = await this.getConfigSignature(url)
        console.log('[WeCom] getConfigSignature 返回:', result)
        return result
      } catch (error) {
        console.error('[WeCom] getConfigSignature 失败:', error)
        throw error
      }
    }

    const getAgentConfigSignatureFn = async (url) => {
      // 注意：根据之前的写法，getAgentConfigSignature 使用 location.href，而不是传入的 url
      const targetUrl = location.href
      console.log('[WeCom] getAgentConfigSignature 被调用, 使用 location.href:', targetUrl)
      try {
        const result = await this.getAgentConfigSignature(targetUrl)
        console.log('[WeCom] getAgentConfigSignature 返回:', result)
        return result
      } catch (error) {
        console.error('[WeCom] getAgentConfigSignature 失败:', error)
        throw error
      }
    }

    // 注册企业微信 JSAPI
    console.log('[WeCom] 开始注册 JSAPI, corpId:', corpId, 'agentId:', agentId)
    return new Promise((resolve, reject) => {
      const registerConfig = {
        corpId,
        agentId,
        jsApiList,
        getConfigSignature: getConfigSignatureFn,
        getAgentConfigSignature: getAgentConfigSignatureFn,
        onConfigSuccess: async (result) => {
          console.log('[WeCom] 企业微信 JSAPI 配置成功', result)
        
          
          onConfigSuccess?.(result)
          resolve()
        },
        onConfigFail: (result) => {
          console.error('[WeCom] 企业微信 JSAPI 配置失败', result)
          onConfigFail?.(result)
          reject(new Error(`企业微信 JSAPI 配置失败: ${JSON.stringify(result)}`))
        }
      }

      console.log('[WeCom] 调用 ww.register()')
      try {
        ww.register(registerConfig)
         // 初始化企业微信开放数据组件（ww-open-data 需要）
         try {
            ww.initOpenData({
              success() {
                console.log('initOpenData success')
              },
              fail(err) {
                console.error('initOpenData failed:', err)
                // 如果是 agentId 错误，记录详细信息
                if (err.errCode === -1 && err.errMsg === 'agentConfig:fail') {
                  console.error('企业微信 agentId 配置错误，请检查:', {
                    agentId,
                    corpId,
                    error: err.err_msg,
                  })
                }
              },
            })
          } catch (error) {
            console.error('企业微信 initOpenData 初始化失败:', error)
          }
        console.log('[WeCom] ww.register() 调用完成（注意：回调是异步的）')
      } catch (error) {
        console.error('[WeCom] ww.register() 调用出错:', error)
        reject(error)
      }
    })
  }

  /**
   * 清除缓存（用于重新初始化）
   */
  clearCache() {
    this.isInitialized = false
    this.initPromise = null
    this.configCache = null
    this.signatureCache.clear()
  }
}

// 创建单例实例
const weComJSAPI = new WeComJSAPI()

/**
 * 初始化企业微信 JSAPI（便捷方法）
 * @param {Object} options - 配置选项
 * @returns {Promise<void>}
 */
export async function initWeCom(options = {}) {
  return await weComJSAPI.init(options)
}

/**
 * 获取 WeComJSAPI 实例
 * @returns {WeComJSAPI}
 */
export function getWeComJSAPI() {
  return weComJSAPI
}

export default weComJSAPI

